import * as React from 'react';
import { ClipboardList, Images, Link2, Play, X } from 'lucide-react';
import type { ProfileButton, ProptreeEnumValue } from '@proptr/shared';
import { cn } from '../lib/utils.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { Select, type SelectOption } from './ui/select.js';
import { GalleryImagesPicker } from './GalleryImagesPicker.js';
import { IconOrImagePicker } from './IconOrImagePicker.js';
import { buildWhatsAppUrl, WhatsAppIcon } from './WhatsAppIcon.js';

// Manager port of the agent app's AddButtonModal. Images (button
// avatar and gallery slides) upload straight to DO Spaces via
// uploadImage.

type ButtonKind = 'link' | 'video' | 'gallery' | 'whatsapp' | 'form';

const KIND_OPTIONS: Array<{
  kind: ButtonKind;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    kind: 'link',
    label: 'Link button',
    description: 'Title, optional subtitle and an emoji icon.',
    Icon: Link2,
  },
  {
    kind: 'whatsapp',
    label: 'WhatsApp',
    description: 'Phone number and an optional predefined message.',
    Icon: WhatsAppIcon,
  },
  {
    kind: 'form',
    label: 'Form button',
    description: 'Open one of your forms when tapped.',
    Icon: ClipboardList,
  },
  {
    kind: 'video',
    label: 'Video',
    description: 'Embed a video. Title plus a video URL.',
    Icon: Play,
  },
  {
    kind: 'gallery',
    label: 'Photo gallery',
    description: 'Multiple images shown in a horizontal slideshow.',
    Icon: Images,
  },
];

export function AddProptreeButtonModal({
  open,
  onClose,
  onAdd,
  formOptions,
  proptreeId,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (button: ProfileButton) => void;
  formOptions: ProptreeEnumValue[];
  // Owning proptree id — passed straight through to IconOrImagePicker
  // so image uploads land in the right Spaces key prefix.
  proptreeId: string;
}) {
  const [kind, setKind] = React.useState<ButtonKind | null>(null);
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) setKind(null);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!shown) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-lg overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="flex items-center justify-between border-b border-[hsl(var(--hairline))] px-5 py-4">
          <div>
            <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
              {kind ? KIND_OPTIONS.find((k) => k.kind === kind)!.label : 'Add button'}
            </h2>
            <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
              {kind
                ? 'Configure how this button looks on your proptree.'
                : 'Pick the type of button you want to add.'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X />
          </Button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
          {kind === null ? (
            <KindPicker onPick={setKind} />
          ) : (
            <KindForm
              kind={kind}
              formOptions={formOptions}
              proptreeId={proptreeId}
              onBack={() => setKind(null)}
              onAdd={(button) => {
                onAdd(button);
                onClose();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function KindPicker({ onPick }: { onPick: (k: ButtonKind) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3">
      {KIND_OPTIONS.map(({ kind, label, description, Icon }) => (
        <button
          key={kind}
          type="button"
          onClick={() => onPick(kind)}
          className="group flex items-start gap-4 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted dark:border-white/10"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-sans text-[14px] font-semibold leading-tight">
              {label}
            </div>
            <p className="mt-1 font-sans text-[12px] leading-snug text-muted-foreground">
              {description}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
}

function newButtonId() {
  return Math.random().toString(36).slice(2, 12);
}

function KindForm({
  kind,
  formOptions,
  proptreeId,
  onBack,
  onAdd,
}: {
  kind: ButtonKind;
  formOptions: ProptreeEnumValue[];
  proptreeId: string;
  onBack: () => void;
  onAdd: (button: ProfileButton) => void;
}) {
  const [title, setTitle] = React.useState(
    kind === 'whatsapp' ? 'WhatsApp' : '',
  );
  const [subtitle, setSubtitle] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [icon, setIcon] = React.useState<string | null>(null);
  const [image, setImage] = React.useState<string | null>(null);
  const [images, setImages] = React.useState<string[]>([]);
  const [waPhone, setWaPhone] = React.useState('');
  const [waMessage, setWaMessage] = React.useState('');
  // For form buttons we store the picked form's id in `url`. Local state
  // mirrors that as a ProptreeEnumValue so the Select can highlight it.
  const [formChoice, setFormChoice] = React.useState<ProptreeEnumValue | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  const titleOk = title.trim().length > 0;
  const videoOk = kind !== 'video' || url.trim().length > 0;
  const waOk = kind !== 'whatsapp' || waPhone.trim().length > 0;
  const formOk = kind !== 'form' || !!formChoice?.value;
  const galleryOk = kind !== 'gallery' || images.length > 0;
  const canSubmit = titleOk && videoOk && waOk && formOk && galleryOk;

  const submit = () => {
    if (!canSubmit) {
      setError(
        kind === 'video' && !videoOk
          ? 'A video URL is required.'
          : kind === 'whatsapp' && !waOk
            ? 'A WhatsApp number is required.'
            : kind === 'form' && !formOk
              ? 'Pick a form to open.'
              : kind === 'gallery' && !galleryOk
                ? 'Add at least one image.'
                : 'Title is required.',
      );
      return;
    }
    const finalUrl =
      kind === 'whatsapp'
        ? buildWhatsAppUrl(waPhone.trim(), waMessage.trim())
        : kind === 'form'
          ? String(formChoice?.value ?? '')
          : url.trim();
    const isWhatsapp = kind === 'whatsapp';
    onAdd({
      id: newButtonId(),
      title: title.trim(),
      subtitle: subtitle.trim() ? subtitle.trim() : null,
      url: finalUrl,
      icon: isWhatsapp ? null : image ? null : icon,
      image: isWhatsapp ? null : image,
      images: kind === 'gallery' ? images : null,
      enabled: true,
      type: kind,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <Label>Title</Label>
        <Input
          className="mt-1"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            kind === 'video'
              ? 'Latest video'
              : kind === 'gallery'
                ? 'Photo gallery'
                : kind === 'whatsapp'
                  ? 'WhatsApp'
                  : 'Button label'
          }
        />
      </div>
      <div>
        <Label>Subtitle (optional)</Label>
        <Input
          className="mt-1"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Short description"
        />
      </div>
      {kind === 'link' && (
        <div>
          <Label>URL (optional)</Label>
          <Input
            className="mt-1"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
          />
        </div>
      )}
      {kind === 'video' && (
        <div>
          <Label>Video URL</Label>
          <Input
            className="mt-1"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… (mp4 or YouTube)"
          />
        </div>
      )}
      {kind === 'whatsapp' && (
        <>
          <div>
            <Label>WhatsApp number</Label>
            <Input
              className="mt-1"
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
              placeholder="+971501234567"
            />
          </div>
          <div>
            <Label>Predefined message (optional)</Label>
            <Input
              className="mt-1"
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              placeholder="Hi! I'd like to ask about…"
            />
          </div>
          <div className="flex items-center gap-3 rounded-md border border-dashed border-border bg-muted/40 px-3 py-2">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25D366]/10 text-[#25D366]">
              <WhatsAppIcon className="h-5 w-5" />
            </span>
            <p className="font-sans text-[12px] text-muted-foreground">
              The WhatsApp icon is fixed for this button type and can't be
              changed.
            </p>
          </div>
        </>
      )}
      {kind === 'form' && (
        <div>
          <Label>Form</Label>
          <Select<string>
            className="mt-1"
            options={formOptions
              .filter((o) => o.value != null)
              .map<SelectOption<string>>((o) => ({
                value: String(o.value),
                label: o.name ?? '—',
              }))}
            value={formChoice?.value != null ? String(formChoice.value) : null}
            onChange={(next) => {
              const orig = formOptions.find(
                (o) => o.value != null && String(o.value) === next,
              );
              if (orig) setFormChoice(orig);
            }}
            placeholder={
              formOptions.length === 0
                ? 'No active forms available'
                : 'Pick a form'
            }
            disabled={formOptions.length === 0}
            searchable={formOptions.length > 5}
          />
        </div>
      )}
      {kind !== 'whatsapp' && (
        <IconOrImagePicker
          icon={icon}
          image={image}
          proptreeId={proptreeId}
          onIconChange={setIcon}
          onImageChange={setImage}
        />
      )}
      {kind === 'gallery' && (
        <GalleryImagesPicker
          images={images}
          onChange={setImages}
          proptreeId={proptreeId}
        />
      )}
      {error && (
        <p className="font-sans text-[12px] text-destructive">{error}</p>
      )}
      <div className="flex items-center justify-between gap-3 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button type="button" size="sm" onClick={submit} disabled={!canSubmit}>
          Add button
        </Button>
      </div>
    </div>
  );
}

